import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Users, Sparkle, HandHeart, Book, Globe, Quotes } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const AboutPSPPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🏳️‍🌈</span>
              <h1 className="text-2xl font-bold text-gradient">
                About PSP Project
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Hero Section */}
        <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
          <div className="text-5xl mb-3">✨</div>
          <h2 className="text-2xl font-bold mb-3">Party Smart Project</h2>
          <p className="text-white/90 leading-relaxed">
            Building safer spaces for 2SLGBTQ+ folks who party and play, by mixing harm reduction with community connection. 💜
          </p>
        </div>

        {/* Guides Built by Community Section */}
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-6 border border-emerald-500/20">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Users size={24} weight="duotone" className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-2">
                  Built by Community, for Community
                </h3>
                <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
                  These PSP guides and info cards were not created randomly. They were built with care, research, and real community voices.
                </p>
              </div>
            </div>
          </div>

          {/* Research Sources */}
          <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--primary))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Globe size={24} weight="duotone" className="text-[rgb(var(--primary))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">
                  Where We Learned
                </h3>
                <p className="text-[rgb(var(--text-secondary))] leading-relaxed mb-4">
                  We studied harm reduction resources from local and international programs, including:
                </p>
                <ul className="space-y-2 text-[rgb(var(--text-secondary))]">
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">🇦🇺</span>
                    PnP and chemsex support groups in <strong className="text-[rgb(var(--text-primary))]">Australia</strong>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">🇬🇧</span>
                    Community-based harm reduction in the <strong className="text-[rgb(var(--text-primary))]">United Kingdom</strong>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">🇺🇸</span>
                    Peer support and public health programs in the <strong className="text-[rgb(var(--text-primary))]">United States</strong>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">🇨🇦</span>
                    Local <strong className="text-[rgb(var(--text-primary))]">Canadian</strong> harm reduction and LGBTQ+ health services
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Real Voices */}
          <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--accent))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Heart size={24} weight="duotone" className="text-[rgb(var(--accent))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">
                  Real Voices, Real Experiences
                </h3>
                <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
                  We listened to real experiences from peers — folks who party, people who support, and people who have lived through comedowns, burnout, psychosis, relapse, recovery, and discrimination in their work based on their use, and everything in between.
                </p>
              </div>
            </div>
          </div>

          {/* What We Noticed */}
          <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--warning))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Book size={24} weight="duotone" className="text-[rgb(var(--warning))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">
                  What We Noticed
                </h3>
                <p className="text-[rgb(var(--text-secondary))] leading-relaxed mb-3">
                  After reviewing many existing guides, we noticed something important. A lot of them are rich in information but:
                </p>
                <ul className="space-y-2 text-[rgb(var(--text-secondary))]">
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--danger))] mr-2">•</span>
                    Heavy in wording
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--danger))] mr-2">•</span>
                    Very clinical and very long
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--danger))] mr-2">•</span>
                    Sometimes hard to read when you're tired, overwhelmed, or in a vulnerable state
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Our Questions */}
          <div className="bg-gradient-to-br from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl p-6 border border-[rgb(var(--primary))]/20">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--primary))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Quotes size={24} weight="duotone" className="text-[rgb(var(--primary))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">
                  So We Asked Ourselves:
                </h3>
                <ul className="space-y-3 text-[rgb(var(--text-primary))]">
                  <li className="text-lg italic">How can we make this easier?</li>
                  <li className="text-lg italic">How can we make this real?</li>
                  <li className="text-lg italic">How can we make something peers will actually read?</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Built It */}
          <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--success))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Sparkle size={24} weight="duotone" className="text-[rgb(var(--success))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">
                  That's How PSP Guides Were Created
                </h3>
                <ul className="space-y-2 text-[rgb(var(--text-secondary))]">
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--success))] mr-2">✓</span>
                    We <strong className="text-[rgb(var(--text-primary))]">simplified</strong> the language
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--success))] mr-2">✓</span>
                    We made it <strong className="text-[rgb(var(--text-primary))]">direct</strong>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--success))] mr-2">✓</span>
                    We kept it <strong className="text-[rgb(var(--text-primary))]">honest</strong>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--success))] mr-2">✓</span>
                    We <strong className="text-[rgb(var(--text-primary))]">removed unnecessary complexity</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Big Message */}
          <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))] to-[rgb(var(--secondary))] rounded-2xl text-white text-center">
            <p className="text-xl font-bold mb-2">
              Because harm reduction only works if people can access it.
            </p>
          </div>

          {/* What is PSP */}
          <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--party))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <Heart size={24} weight="duotone" className="text-[rgb(var(--party))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-2">
                  What is the PSP Project?
                </h3>
                <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
                  The Party Smart Project (PSP) is a community-driven harm reduction initiative created by and for 2SLGBTQ+ people who engage in Party & Play (PnP) / Chemsex. We believe everyone deserves access to non-judgmental support and practical information to stay safer while partying.
                </p>
              </div>
            </div>
          </div>

          {/* Personal Responsibility + Peer Support */}
          <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(var(--secondary))]/20 flex items-center justify-center mr-4 flex-shrink-0">
                <HandHeart size={24} weight="duotone" className="text-[rgb(var(--secondary))]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-2">
                  Personal Responsibility + Peer Support
                </h3>
                <ul className="space-y-2 text-[rgb(var(--text-secondary))]">
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">•</span>
                    Know your limits
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">•</span>
                    Use harm reduction supplies
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">•</span>
                    Check on your friends
                  </li>
                  <li className="flex items-start">
                    <span className="text-[rgb(var(--primary))] mr-2">•</span>
                    Step in if someone's not safe
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer Message */}
          <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
            <p className="text-[rgb(var(--text-primary))] font-bold text-lg mb-2">
              Party Smart. Stay Safe. Stay Connected.
            </p>
            <p className="text-[rgb(var(--text-secondary))] text-sm">
              Harm reduction = self-respect + community care 💜
            </p>
          </div>

          {/* Explore More */}
          <div className="space-y-3 mt-8">
            <Button
              onClick={() => navigate('/psp-info-cards')}
              className="w-full min-h-[56px] rounded-full party-gradient text-white font-semibold"
            >
              <span className="text-xl mr-2">📋</span>
              Explore PSP Info Cards
            </Button>
            <Button
              onClick={() => navigate('/psp-guides')}
              variant="outline"
              className="w-full min-h-[56px] rounded-full border-[rgb(var(--primary))]/50 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10 font-semibold"
            >
              <span className="text-xl mr-2">📚</span>
              PSP Guides
            </Button>
            <Button
              onClick={() => navigate('/resources')}
              variant="outline"
              className="w-full min-h-[56px] rounded-full border-[rgb(var(--accent))]/50 text-[rgb(var(--accent))] hover:bg-[rgb(var(--accent))]/10 font-semibold"
            >
              <span className="text-xl mr-2">🌍</span>
              Find Community Resources
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AboutPSPPage;
